import React from 'react';
import {useNavigate} from 'react-router';
import {diApi} from '@di-shared/api/di';
import Header from '@di-shared/features/common/Header';
import HeaderCloseButton from '@di-shared/features/common/HeaderCloseButton';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useMileageRegister from '@di-shared/features/mileage/register/useMileageRegister';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {subDate} from '@shared/dateUtils';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';

interface MileageRegisterProps {
  vin: string;
}

function MileageRegister({vin}: MileageRegisterProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const {openAlert} = useCcWebviewUtilsHelper();
  const {drvngDstncRgsde, drvngDstncEndde, drvng_stt_cd} = useMileageRegister(vin);
  const {resolveError} = useApiError();

  const [register] = usePendingHandler(
    async () => {
      WebviewUtils.loading(true);
      await diApi.ecoMileageController.updateVehicleLink({vin, requestBody: {vin}});
      await openAlert({
        title: t('T30306'),
        message: t('T30307'),
      });
      if (drvng_stt_cd === '051') {
        /**
         * [2026-01-22:Jeyoung Lee] FIXME: 051 실적등록 후 최초주행거리 등록으로 이동
         * @link https://hmg.dooray.com/project/tasks/4208274598827184933
         */
        navigate(ROUTE_PATHS.MILEAGE_FIRST_REGISTER.getFullPath(vin), {replace: true});
      } else {
        navigate(ROUTE_PATHS.MILEAGE_PARTICIPATION_GUIDE.getFullPath(vin), {replace: true});
      }
      WebviewUtils.loading(false);
    },
    async (error) => {
      WebviewUtils.loading(false);
      await resolveError(error);
    }
  );
  return (
    <MileageRegisterCase1_1
      drvngDstncRgsde={drvngDstncRgsde}
      drvngDstncEndde={drvngDstncEndde}
      onRegister={register}
    />
  );
}

export default MileageRegister;

export interface MileageRegisterCaseProps {
  drvngDstncRgsde: Date | null;
  drvngDstncEndde: Date | null;
  onRegister: () => void;
}

export function MileageRegisterCase1_1({drvngDstncRgsde, drvngDstncEndde, onRegister}: MileageRegisterCaseProps) {
  const {openConfirm, backOrClose} = useCcWebviewUtilsHelper();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  return (
    <>
      <Header
        right={
          <HeaderCloseButton
            onClick={async () => {
              const isConfirm = await openConfirm({
                title: t('T30278'),
                message: t('T30186_$date', {date: drvngDstncEndde ? CldrDatetimeFormatter.format(drvngDstncEndde, 'MMMd') : ''}),
                okText: t('T30279'),
                cancelText: t('T30280'),
              });
              // 예 아니오 버튼 반대
              if (isConfirm) {
                backOrClose();
              }
            }}
          />
        }
      >
        <h1 className="title_xsmall_b nav_center_title">{t('T10122')}</h1>
      </Header>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <section className="section section_mileage_submit">
          <div className="section_heading type_main">
            <h2
              className="section_heading_title"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T30281')}
            </h2>
            <p className="section_heading_desc">
              {t('T30185_$date', {
                date: drvngDstncRgsde ? CldrDatetimeFormatter.format(subDate(drvngDstncRgsde, {days: 1}), 'MMMd') : '-',
              })}
            </p>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="floating_guide_box">
          <div className="floating_guide_icon">
            <span className="ic ic_warning_24px"></span>
          </div>
          <span className="floating_guide_text">
            {t('T30186_$date', {date: drvngDstncEndde ? CldrDatetimeFormatter.format(drvngDstncEndde, 'MMMd') : '-'})}
          </span>
        </div>

        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          onClick={onRegister}
        >
          {t('T30187')}
        </button>
      </div>
    </>
  );
}

export function MileageRegisterLoading() {
  const {backOrClose} = useCcWebviewUtilsHelper();
  const {t} = useInferableTranslation();
  return (
    <>
      <Header
        right={
          <HeaderCloseButton
            onClick={() => {
              backOrClose();
            }}
          />
        }
      >
        <h1 className="title_xsmall_b nav_center_title">{t('T10122')}</h1>
      </Header>
      <main
        id="content"
        className="content page_eco_mileage"
      >
        <section className="section section_mileage_submit">
          <div className="section_heading type_main">
            <h2
              className="section_heading_title skeleton"
              style={{height: 60}}
            />
            <p
              className="section_heading_desc skeleton"
              style={{height: 36}}
            />
          </div>
          <div
            className="mileage_submit_box skeleton"
            style={{height: 122}}
          />
          <div
            className="mileage_submit_box skeleton"
            style={{height: 122}}
          />
        </section>
      </main>
      <div className="floating_action_area">
        <button
          type="button"
          className="btn btn_large btn_filled btn_full"
          disabled
        >
          {t('T30187')}
        </button>
      </div>
    </>
  );
}
